import React, { useMemo, useState } from "react";
import { Badge, Card, SectionLabel } from "./ProfileDashboardUI";

const RFQ_WORKSPACE_PATH = "/rfqs/workspace/my-workspace";

const RFQ_TYPES = [
  { rfqType: "material", label: "Material", tone: "blue" },
  { rfqType: "contractor", label: "Contracting", tone: "amber" },
  { rfqType: "labour", label: "Labour", tone: "purple" },
];

const buildWorkspaceUrl = ({ rfqType, disciplineId, categoryId, subCategoryId }) => {
  const params = new URLSearchParams({ status: "ALL" });

  if (rfqType) params.set("rfqType", rfqType);
  if (disciplineId) params.set("disciplineId", disciplineId);
  if (categoryId) params.set("categoryId", categoryId);
  if (subCategoryId) params.set("subCategoryId", subCategoryId);

  return `${RFQ_WORKSPACE_PATH}?${params.toString()}`;
};

const toNumber = (value = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatCurrency = (value = 0) => {
  const amount = toNumber(value);
  if (amount === 0) return "Rs. 0";

  const formatter = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const absoluteAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  if (absoluteAmount >= 10000000) return `${sign}Rs. ${formatter.format(absoluteAmount / 10000000)} Cr`;
  if (absoluteAmount >= 100000) return `${sign}Rs. ${formatter.format(absoluteAmount / 100000)} L`;
  return `${sign}Rs. ${formatter.format(absoluteAmount)}`;
};

const normalizeText = (value = "") => String(value || "").toLowerCase().trim();

const getClosingAmount = (node = {}) =>
  toNumber(node.closingAmount ?? node.closureValue ?? node.totalClosingAmount ?? node.totalClosureValue);

const getTypeMeta = (rfqType) =>
  RFQ_TYPES.find((type) => type.rfqType === rfqType) || { rfqType, label: rfqType || "Other", tone: "gray" };

const getOrangeSurface = (tokens) => (tokens.page === "#111315" ? "#3A2416" : "#FFF0E6");

const buildTypeTotals = (typeTotals = [], tree = []) =>
  RFQ_TYPES.map((type) => {
    const total = typeTotals.find((item) => item.rfqType === type.rfqType) || {};
    const treeNode = tree.find((item) => item.rfqType === type.rfqType) || {};

    return {
      ...type,
      count: toNumber(total.count ?? treeNode.count),
      closingAmount: getClosingAmount(total) || getClosingAmount(treeNode),
    };
  });

const categoryMatchesSearch = (category, searchTerm) => {
  if (!searchTerm) return true;

  const haystack = [
    category.categoryName,
    ...(category.subCategories || []).map((subCategory) => subCategory.subCategoryName),
  ].map(normalizeText).join(" ");

  return haystack.includes(searchTerm);
};

const filterTree = (tree = [], searchTerm = "") => {
  const normalizedSearch = normalizeText(searchTerm);
  if (!normalizedSearch) return tree;

  return tree
    .map((typeNode) => ({
      ...typeNode,
      disciplines: (typeNode.disciplines || [])
        .map((discipline) => {
          const disciplineMatches = normalizeText(discipline.disciplineName).includes(normalizedSearch);
          const categories = disciplineMatches
            ? discipline.categories
            : (discipline.categories || []).filter((category) => categoryMatchesSearch(category, normalizedSearch));

          return { ...discipline, categories };
        })
        .filter((discipline) => discipline.categories.length > 0),
    }))
    .filter((typeNode) => typeNode.disciplines.length > 0);
};

const NavigateButton = ({ children = "View >", onClick, tokens, compact = false }) => (
  <button
    type="button"
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick?.();
    }}
    className={`inline-flex items-center justify-center rounded-full border font-semibold transition-all hover:-translate-y-0.5 hover:shadow-sm ${
      compact ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-[11px]"
    }`}
    style={{
      borderColor: tokens.primaryMid,
      backgroundColor: tokens.primaryLight,
      color: tokens.primary,
    }}
  >
    {children}
  </button>
);

const TypeSummaryCard = ({ type, tokens, onNavigate }) => {
  const disabled = !type.count;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onNavigate?.(buildWorkspaceUrl({ rfqType: type.rfqType }))}
      className={`group rounded-2xl border p-4 text-left transition-all ${
        disabled ? "cursor-default opacity-80" : "hover:-translate-y-0.5 hover:shadow-md"
      }`}
      style={{
        borderColor: tokens.line,
        backgroundColor: tokens.card,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[14px] font-bold" style={{ color: tokens.ink }}>
            {type.label}
          </p>
        </div>
        <Badge variant={type.tone} tokens={tokens}>
          {type.count} RFQs
        </Badge>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <p className="text-[20px] font-bold" style={{ color: tokens.ink }}>
            {formatCurrency(type.closingAmount)}
          </p>
          <p className="mt-1 text-[11px] font-semibold" style={{ color: tokens.faint }}>
            Total closure value (closed RFQs)
          </p>
        </div>
        {!disabled && (
          <span
            className="text-[11px] font-semibold transition-colors group-hover:text-[#FD6600]"
            style={{ color: tokens.primary }}
          >
            View RFQs
          </span>
        )}
      </div>
    </button>
  );
};

const TypeTab = ({ type, active, tokens, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full border px-3.5 py-2 text-[12px] font-semibold transition-all"
    style={{
      borderColor: active ? tokens.brand : tokens.line,
      backgroundColor: active ? getOrangeSurface(tokens) : tokens.card,
      color: active ? tokens.brand : tokens.muted,
    }}
  >
    {type.label} <span className="ml-1 opacity-75">({type.count})</span>
  </button>
);

const StatPill = ({ label, value, tokens, tone = "default" }) => {
  const toneMap = {
    rfq: { backgroundColor: tokens.primaryLight, color: tokens.primary, borderColor: tokens.primaryMid },
    closure: { backgroundColor: getOrangeSurface(tokens), color: tokens.brand, borderColor: "#FFD6B8" },
    default: { backgroundColor: tokens.cardSoft, color: tokens.ink, borderColor: tokens.line },
  };
  const styles = toneMap[tone] || toneMap.default;

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
      style={styles}
    >
      <span className="font-semibold opacity-75">{label}</span>
      {value}
    </span>
  );
};

const getTypeSummary = (typeNode = {}) => {
  const disciplines = typeNode.disciplines || [];
  const categories = disciplines.flatMap((discipline) => discipline.categories || []);
  const subCategories = categories.flatMap((category) => category.subCategories || []);

  return {
    disciplineCount: disciplines.length,
    categoryCount: categories.length,
    subCategoryCount: subCategories.length,
  };
};

const SummaryMetricCard = ({ label, value, tokens }) => (
  <div
    className="rounded-xl border px-3 py-2"
    style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}
  >
    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: tokens.faint }}>
      {label}
    </p>
    <p className="mt-1 text-[14px] font-bold" style={{ color: tokens.ink }}>
      {value}
    </p>
  </div>
);

const SubCategoryRow = ({ rfqType, discipline, category, subCategory, tokens, onNavigate }) => (
  <button
    type="button"
    onClick={() => onNavigate?.(buildWorkspaceUrl({
      rfqType,
      disciplineId: discipline.disciplineId,
      categoryId: category.categoryId,
      subCategoryId: subCategory.subCategoryId,
    }))}
    className="group inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm"
    style={{
      borderColor: tokens.line,
      backgroundColor: tokens.card,
      color: tokens.ink,
    }}
  >
    <span className="truncate text-[11px] font-semibold">
        {subCategory.subCategoryName || "Uncategorized"}
    </span>
    <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: tokens.cardSoft, color: tokens.muted }}>
      {subCategory.count || 0} RFQs
    </span>
    <span className="text-[10px] font-semibold" style={{ color: tokens.brand }}>
      {formatCurrency(getClosingAmount(subCategory))}
    </span>
    <span className="text-[10px] font-semibold opacity-0 transition-opacity group-hover:opacity-100" style={{ color: tokens.primary }}>
      View
    </span>
  </button>
);

const CategoryRow = ({ rfqType, discipline, category, tokens, onNavigate }) => {
  const subCategories = category.subCategories || [];

  return (
    <div
      className="rounded-xl border p-3 transition-shadow hover:shadow-sm"
      style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold" style={{ color: tokens.ink }}>
            {category.categoryName || "Uncategorized"}
          </p>
          <p className="text-[10px]" style={{ color: tokens.faint }}>
            Category {subCategories.length ? `- ${subCategories.length} subcategories` : ""}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatPill label="RFQs" value={category.count || 0} tokens={tokens} tone="rfq" />
          <StatPill label="Closure" value={formatCurrency(getClosingAmount(category))} tokens={tokens} tone="closure" />
          <NavigateButton
            compact
            tokens={tokens}
            onClick={() => onNavigate?.(buildWorkspaceUrl({
              rfqType,
              disciplineId: discipline.disciplineId,
              categoryId: category.categoryId,
            }))}
          />
        </div>
      </div>

      {subCategories.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {subCategories.map((subCategory) => (
            <SubCategoryRow
              key={`${category.categoryId}-${subCategory.subCategoryId || subCategory.subCategoryName}`}
              rfqType={rfqType}
              discipline={discipline}
              category={category}
              subCategory={subCategory}
              tokens={tokens}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DisciplinePanel = ({ typeNode, discipline, tokens, onNavigate, defaultOpen }) => (
  <details
    className="group rounded-2xl border"
    style={{ borderColor: tokens.line, backgroundColor: tokens.card }}
    open={defaultOpen}
  >
    <summary
      className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 group-open:rounded-b-none"
      style={{ background: `linear-gradient(90deg, ${tokens.cardSoft}, ${tokens.card})` }}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-[14px] font-semibold" style={{ color: tokens.ink }}>
            {discipline.disciplineName || "Uncategorized"}
          </p>
          <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: tokens.card, color: tokens.faint }}>
            Click to expand
          </span>
        </div>
        <p className="mt-1 text-[10px]" style={{ color: tokens.faint }}>
          Discipline - {(discipline.categories || []).length} categories mapped
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatPill label="RFQs" value={discipline.count || 0} tokens={tokens} tone="rfq" />
        <StatPill label="Closure" value={formatCurrency(getClosingAmount(discipline))} tokens={tokens} tone="closure" />
        <NavigateButton
          tokens={tokens}
          onClick={() => onNavigate?.(buildWorkspaceUrl({
            rfqType: typeNode.rfqType,
            disciplineId: discipline.disciplineId,
          }))}
        />
      </div>
    </summary>

    <div className="space-y-2 border-t p-3" style={{ borderColor: tokens.line }}>
      {(discipline.categories || []).map((category) => (
        <CategoryRow
          key={`${discipline.disciplineId}-${category.categoryId || category.categoryName}`}
          rfqType={typeNode.rfqType}
          discipline={discipline}
          category={category}
          tokens={tokens}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  </details>
);

const CategoryActivity = ({ stats, tokens, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeType, setActiveType] = useState("material");
  const categoryActivity = stats?.categoryActivity || {};
  const sourceTree = useMemo(() => categoryActivity.tree || [], [categoryActivity.tree]);
  const typeTotals = useMemo(
    () => buildTypeTotals(categoryActivity.typeTotals || [], sourceTree),
    [categoryActivity.typeTotals, sourceTree],
  );
  const filteredTree = useMemo(() => filterTree(sourceTree, searchTerm), [sourceTree, searchTerm]);
  const activeTypeNode = filteredTree.find((typeNode) => typeNode.rfqType === activeType);
  const activeTypeMeta = typeTotals.find((type) => type.rfqType === activeType) || getTypeMeta(activeType);
  const activeTypeSummary = getTypeSummary(activeTypeNode);

  return (
    <>
      <SectionLabel tokens={tokens}>Category activity</SectionLabel>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {typeTotals.map((type) => (
          <TypeSummaryCard
            key={type.rfqType}
            type={type}
            tokens={tokens}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      <Card tokens={tokens} className="mt-3 p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: tokens.line }}>
          <div>
            <p className="text-[14px] font-semibold" style={{ color: tokens.ink }}>
              Category performance drill-down
            </p>
            <p className="text-[11px]" style={{ color: tokens.faint }}>
              Expand disciplines to view category and subcategory RFQ counts with closure value.
            </p>
          </div>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search discipline, category, subcategory"
            className="h-9 w-full rounded-lg border px-3 text-[12px] font-semibold outline-none md:w-[340px]"
            style={{
              borderColor: tokens.line,
              backgroundColor: tokens.card,
              color: tokens.ink,
            }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3" style={{ borderColor: tokens.line }}>
          {typeTotals.map((type) => (
            <TypeTab
              key={type.rfqType}
              type={type}
              active={activeType === type.rfqType}
              tokens={tokens}
              onClick={() => setActiveType(type.rfqType)}
            />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 border-b p-3 md:grid-cols-4" style={{ borderColor: tokens.line }}>
          <SummaryMetricCard label="Selected view" value={activeTypeMeta.label} tokens={tokens} />
          <SummaryMetricCard label="Disciplines" value={activeTypeSummary.disciplineCount} tokens={tokens} />
          <SummaryMetricCard label="Categories" value={activeTypeSummary.categoryCount} tokens={tokens} />
          <SummaryMetricCard label="Subcategories" value={activeTypeSummary.subCategoryCount} tokens={tokens} />
        </div>

        <div className="dashboard-print-expand-scroll max-h-[560px] space-y-2 overflow-y-auto p-3">
          {!activeTypeNode?.disciplines?.length ? (
            <div className="rounded-xl border py-8 text-center" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
              <p className="text-[13px] font-bold" style={{ color: tokens.ink }}>
                No {activeTypeMeta.label.toLowerCase()} categories found
              </p>
              <p className="mt-1 text-[11px]" style={{ color: tokens.faint }}>
                Try clearing search or wait for category-backed RFQs to be created.
              </p>
            </div>
          ) : (
            activeTypeNode.disciplines.map((discipline, index) => (
              <DisciplinePanel
                key={`${activeTypeNode.rfqType}-${discipline.disciplineId || discipline.disciplineName}`}
                typeNode={activeTypeNode}
                discipline={discipline}
                tokens={tokens}
                onNavigate={onNavigate}
                defaultOpen={index < 2}
              />
            ))
          )}
        </div>
      </Card>
    </>
  );
};

export default CategoryActivity;
